import React, { useState } from "react";
import { Modal, FormInput, Button } from "@/components/ui";
import { UploadCloud, X } from "lucide-react";
import { projectService } from "../services/projectService";
import { showApiError, showSuccessMessage } from "@/lib/toast";

export const ProjectMediaUploadModal = ({ isOpen, onClose, project, onSuccess, docType }) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);

      const newPreviews = newFiles.map(file => {
        if (file.type.startsWith('image/')) {
          return URL.createObjectURL(file);
        }
        return null;
      });
      setPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const newPreviews = [...prev];
      if (newPreviews[index]) {
        URL.revokeObjectURL(newPreviews[index]);
      }
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return;

    setIsSubmitting(true);
    
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (title.trim()) fd.append("documentName", title.trim());
      if (description.trim()) fd.append("notes", description.trim());
      if (docType) fd.append("docType", docType);
      
      const updated = await projectService.uploadBalanceDocuments(
        project._id,
        fd
      );
      
      showSuccessMessage("Documents uploaded successfully!");
      setFiles([]);
      setPreviews([]);
      setTitle("");
      setDescription("");
      if (onSuccess) onSuccess(updated);
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalTitle = () => {
    if (docType === 'gallery') return "UPLOAD PROJECT PROGRESS IMAGES";
    if (docType === 'design') return "UPLOAD PROJECT DESIGNS";
    return "UPLOAD PROJECT DOCUMENTS";
  };

  const getInputLabel = () => {
    if (docType === 'gallery') return "Image Name / Title";
    if (docType === 'design') return "Design Name / Title";
    return "Document Name / Title";
  };

  const getPlaceholder = () => {
    if (docType === 'gallery') return "e.g. Site Visit Progress Photos";
    if (docType === 'design') return "e.g. 3D Kitchen Renders";
    return "e.g. Signed Contract Agreement";
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={getModalTitle()} 
      size="md"
      footer={
        <div className="flex gap-3 w-full">
          <Button variant="outline" className="flex-1" onClick={onClose} type="button">Cancel</Button>
          <Button
            type="submit"
            form="upload-project-form"
            variant="orange"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting || files.length === 0}
          >
            {docType === 'gallery' ? 'Upload Images' : docType === 'design' ? 'Upload Designs' : 'Upload Documents'}
          </Button>
        </div>
      }
    >
      <form id="upload-project-form" onSubmit={onFormSubmit} className="flex flex-col gap-3 pb-1">
        
        <FormInput
          label={getInputLabel()}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={getPlaceholder()}
          size="sm"
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">Notes (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-brand-blue/20 resize-none h-10"
            placeholder="Add details about this media..."
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">Media Files <span className="text-red-500">*</span></label>
          
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer relative min-h-[80px]">
            <input 
              type="file" 
              multiple
              accept={docType === 'gallery' ? "image/*" : undefined}
              onChange={handleFileChange} 
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              required={files.length === 0}
            />
            <UploadCloud className="w-6 h-6 text-slate-400 mb-1" />
            <span className="text-sm font-medium text-slate-700">
              {files.length > 0 ? "Click or drag to add more files" : "Click or drag file to upload"}
            </span>
            <span className="text-xs text-slate-500 mt-1">{docType === 'gallery' ? 'Supports image files (jpg, png, webp)' : 'Supports any file type'}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 mt-1 w-full h-[100px] min-h-[100px] max-h-[100px]">
          <label className="text-[11px] font-bold text-slate-400 tracking-wider mb-2 uppercase">Uploaded Files</label>
          
          {files.length > 0 ? (
            <div className="flex flex-col gap-3 h-full overflow-y-auto pr-1 custom-scrollbar">
              {files.map((file, idx) => (
                <div key={idx} className="group flex items-center bg-slate-50 p-3 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all gap-4 w-full relative">
                  <button 
                    type="button" 
                    onClick={() => handleRemoveFile(idx)}
                    className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-500 hover:text-red-500 hover:bg-red-50 transition-colors shadow-sm z-10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  
                  <div className="w-10 h-10 rounded-lg bg-white border border-slate-100 flex items-center justify-center overflow-hidden shrink-0 p-1.5">
                    {previews[idx] ? (
                      <img src={previews[idx]} alt="Preview" className="w-full h-full object-cover rounded" />
                    ) : (
                      file.type.startsWith('video/') ? <span className="text-lg">🎬</span> : <span className="text-lg">📄</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-slate-700 truncate mb-0.5" title={file.name}>
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-medium">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 text-xs font-medium bg-slate-50/50 rounded-lg border border-slate-100 border-dashed">
              No files selected yet
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};
